'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard, Users, FileText, Award, GitBranch,
  BarChart2, ChevronLeft, ChevronRight, Settings, Bell, LogOut,
} from 'lucide-react';

const NAV_ITEMS = [
  { href: '/admin-network-dashboard', icon: LayoutDashboard, label: 'Network Dashboard', badge: 3 },
  { href: '/distributor-profile-detail', icon: Users, label: 'Distributor Profiles', badge: null },
  { href: '/commission-reports', icon: FileText, label: 'Commission Reports', badge: null },
  { href: '/rank-title-qualification-tracker', icon: Award, label: 'Rank Tracker', badge: 5 },
  { href: '/breakaway-generation-commission-explorer', icon: GitBranch, label: 'Generation Explorer', badge: null },
  { href: '/network-performance-analytics', icon: BarChart2, label: 'Performance Analytics', badge: null },
];

interface SidebarProps {
  currentPath?: string;
}

export default function Sidebar({ currentPath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={`relative flex flex-col bg-zinc-900 border-r border-zinc-800 transition-all duration-300 ease-in-out flex-shrink-0 ${collapsed ? 'w-16' : 'w-60'}`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-2.5 px-4 py-4 border-b border-zinc-800 ${collapsed ? 'justify-center px-0' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <div className="flex flex-col leading-tight overflow-hidden">
            <span className="font-semibold text-sm text-zinc-100 truncate">NuSkinSuite</span>
            <span className="text-[10px] text-zinc-500 truncate">Admin Console</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto scrollbar-thin">
        {!collapsed && (
          <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 px-2 pb-2">Navigation</p>
        )}
        {NAV_ITEMS.map((item) => {
          const isActive = currentPath === item.href;
          return (
            <Link
              key={`nav-${item.href}`}
              href={item.href}
              title={collapsed ? item.label : undefined}
              className={`group flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm transition-all duration-150 ${
                isActive
                  ? 'bg-violet-600/20 text-violet-300 border border-violet-500/20'
                  : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60'
              } ${collapsed ? 'justify-center px-0 py-2.5' : ''}`}
            >
              <item.icon size={18} className={`flex-shrink-0 ${isActive ? 'text-violet-400' : ''}`} />
              {!collapsed && (
                <>
                  <span className="flex-1 truncate font-medium">{item.label}</span>
                  {item.badge && (
                    <span className="flex-shrink-0 bg-violet-600 text-white text-[10px] font-semibold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className={`border-t border-zinc-800 py-3 px-2 space-y-0.5`}>
        {[
          { icon: Bell, label: 'Notifications' },
          { icon: Settings, label: 'Settings' },
          { icon: LogOut, label: 'Sign Out' },
        ].map((item) => (
          <button
            key={`bottom-${item.label}`}
            title={collapsed ? item.label : undefined}
            className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/60 transition-all duration-150 ${collapsed ? 'justify-center px-0' : ''}`}
          >
            <item.icon size={17} className="flex-shrink-0" />
            {!collapsed && <span className="font-medium">{item.label}</span>}
          </button>
        ))}

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-xs text-zinc-600 hover:text-zinc-300 hover:bg-zinc-800/40 transition-all duration-150 mt-1 ${collapsed ? 'justify-center px-0' : ''}`}
        >
          {collapsed ? <ChevronRight size={15} /> : (
            <>
              <ChevronLeft size={15} />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}